import { Link } from "react-router-dom";
import { Phone, ArrowRight, ShieldCheck, IndianRupee, Clock, MapPinned, Users, Briefcase, Star } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import Seo from "@/components/Seo";
import BookingForm from "@/components/BookingForm";
import FaqSection from "@/components/sections/FaqSection";
import CtaBand from "@/components/sections/CtaBand";
import ReviewsSection from "@/components/sections/ReviewsSection";
import { SectionHeading, DynIcon } from "@/components/ui-bits";
import { useServices, useVehicles, usePackages } from "@/hooks/usePublicData";
import { WHY_US, POPULAR_ROUTES, CITY_LINKS, HOME_FAQS } from "@/data/site";
import { useSite, telLink, waLink } from "@/context/SiteContext";

const HERO_IMG = "/images/78aa5d7f128ed2ab894cdc48fb3ded581cb0b1b39ac9662625456b9cdc1306fd.jpeg";
const WHY_ICONS = { ShieldCheck, IndianRupee, Clock, MapPinned };
const CITY_IMG = {
  Jodhpur: "https://images.unsplash.com/photo-1602643454724-21d5a40722db?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  Jaipur: "https://images.unsplash.com/photo-1603262110263-fb0112e7cc33?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  Udaipur: "https://images.unsplash.com/photo-1589901164570-f9de6556e1c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  Jaisalmer: "https://images.pexels.com/photos/35130760/pexels-photo-35130760.jpeg?auto=compress&cs=tinysrgb&w=800",
};

export default function Home() {
  const s = useSite();
  const { data: services = [] } = useServices();
  const { data: vehicles = [] } = useVehicles();
  const { data: packages = [] } = usePackages();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TaxiService",
    name: "Chouhan Taxi Service",
    image: `https://chouhantaxiservice.com${s.logo}`,
    url: "https://chouhantaxiservice.com",
    telephone: "+919982903736",
    address: { "@type": "PostalAddress", streetAddress: "Mehrangarh Fort Road", addressLocality: "Jodhpur", addressRegion: "Rajasthan", postalCode: "342024", addressCountry: "IN" },
    areaServed: ["Jodhpur", "Jaipur", "Udaipur", "Jaisalmer"],
    serviceType: ["Local Taxi", "Airport Taxi", "Outstation Taxi", "One Way Taxi", "Round Trip Taxi", "Tour & Travel"],
  };

  return (
    <>
      <Seo title="Taxi Service in Jodhpur, Jaipur, Udaipur & Jaisalmer | Chouhan Taxi Service"
        description="Book reliable taxi service in Jodhpur, Jaipur, Udaipur and Jaisalmer with Chouhan Taxi Service. Local, airport, one-way, round-trip and outstation cab services."
        path="/" jsonLd={jsonLd} />

      {/* HERO */}
      <section className="relative bg-navy" data-testid="hero-section">
        <img src={HERO_IMG} alt="Chouhan Taxi Service car on a scenic Rajasthan highway near a fort" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative container-x pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="max-w-2xl animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-5 border border-white/20">
              <Star className="w-3.5 h-3.5 text-saffron fill-saffron" /> Trusted Rajasthan Taxi & Tour Service
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-heading font-extrabold text-white leading-tight tracking-tight">
              Taxi Service in Jodhpur, Jaipur, Udaipur & Jaisalmer
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-200 leading-relaxed max-w-xl">
              Book reliable local, airport and outstation taxi services with Chouhan Taxi Service. Comfortable cars, professional drivers and convenient travel across Jodhpur, Jaipur, Udaipur and Jaisalmer.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book-taxi" className="inline-flex items-center gap-2 bg-brand hover:bg-royal text-white font-semibold px-7 py-3.5 rounded-full transition-all active:scale-95 shadow-lg shadow-brand/30" data-testid="hero-book-btn">Book a Taxi <ArrowRight className="w-4 h-4" /></Link>
              <a href={telLink(s)} className="inline-flex items-center gap-2 bg-white text-navy font-semibold px-7 py-3.5 rounded-full hover:bg-brand-light transition-colors" data-testid="hero-call-btn"><Phone className="w-4 h-4" /> Call Now</a>
              <a href={waLink(s)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-whatsapp text-white font-semibold px-7 py-3.5 rounded-full hover:brightness-95 transition-all" data-testid="hero-whatsapp-btn"><WhatsAppIcon className="w-5 h-5" /> Book on WhatsApp</a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-200">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brand-sky" /> Verified Drivers</span>
              <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-brand-sky" /> 24/7 Service</span>
              <span className="flex items-center gap-2"><IndianRupee className="w-4 h-4 text-brand-sky" /> Transparent Fares</span>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK BOOKING FORM */}
      <section className="relative bg-slate-50" data-testid="quick-booking-section">
        <div className="container-x">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 -mt-14 relative z-20 p-6 sm:p-8 max-w-5xl mx-auto">
            <div className="mb-5">
              <div className="text-xs uppercase font-bold tracking-[0.2em] text-brand mb-1">Quick Booking</div>
              <h2 className="text-2xl font-heading font-bold text-navy-heading">Book Your Taxi in Seconds</h2>
            </div>
            <BookingForm />
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="section-pad bg-slate-50" data-testid="why-us-section">
        <div className="container-x">
          <SectionHeading center eyebrow="Why Choose Us" title="Why Choose Chouhan Taxi Service"
            subtitle="A locally rooted Jodhpur taxi company committed to safe, comfortable and affordable travel across Rajasthan." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_US.map((w) => {
              const Icon = WHY_ICONS[w.icon];
              return (
                <div key={w.title} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm card-hover text-center">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-light flex items-center justify-center mb-4"><Icon className="w-7 h-7 text-brand" /></div>
                  <h3 className="font-heading font-semibold text-navy-heading text-lg mb-2">{w.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-pad bg-white" data-testid="services-section">
        <div className="container-x">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <SectionHeading eyebrow="Our Services" title="Our Taxi Services" subtitle="From quick city rides to multi-day Rajasthan tours, we have a service for every journey." />
            <Link to="/taxi-services" className="hidden sm:inline-flex items-center gap-1 text-brand font-semibold hover:gap-2 transition-all">View all services <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.slice(0, 8).map((sv) => (
              <Link key={sv.slug} to={`/taxi-services/${sv.slug}`} data-testid={`service-card-${sv.slug}`}
                className="group bg-white rounded-2xl p-6 border border-slate-100 shadow-sm card-hover">
                <div className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center mb-4 group-hover:bg-brand transition-colors"><DynIcon name={sv.icon} className="w-6 h-6 text-white" /></div>
                <h3 className="font-heading font-semibold text-navy-heading text-lg mb-2">{sv.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">{sv.short}</p>
                <span className="text-sm text-brand font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">Learn more <ArrowRight className="w-3.5 h-3.5" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* POPULAR ROUTES */}
      <section className="section-pad bg-slate-50" data-testid="routes-section">
        <div className="container-x">
          <SectionHeading center eyebrow="Popular Routes" title="Popular Taxi Routes" subtitle="Comfortable taxis on Rajasthan's most travelled intercity routes." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {POPULAR_ROUTES.map((r) => (
              <Link key={r.slug} to={`/routes/${r.slug}`} data-testid={`route-card-${r.slug}`}
                className="group bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm card-hover">
                <div className="relative h-40 overflow-hidden">
                  <img src={r.image} alt={`${r.from} to ${r.to} taxi`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-semibold text-navy-heading text-lg flex items-center gap-2">{r.from} <ArrowRight className="w-4 h-4 text-brand" /> {r.to}</h3>
                  <div className="mt-2 flex gap-4 text-sm text-slate-500"><span>{r.distance}</span><span>·</span><span>{r.time} drive</span></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FLEET */}
      <section className="section-pad bg-white" data-testid="fleet-section">
        <div className="container-x">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <SectionHeading eyebrow="Our Fleet" title="Our Taxi Fleet" subtitle="Clean, well-maintained and air-conditioned vehicles for every group size." />
            <Link to="/fleet" className="hidden sm:inline-flex items-center gap-1 text-brand font-semibold hover:gap-2 transition-all">View fleet <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {vehicles.map((v) => (
              <div key={v.id} className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm card-hover" data-testid={`fleet-card-${v.category}`}>
                <div className="h-40 overflow-hidden bg-slate-100"><img src={v.image} alt={`${v.name} taxi`} loading="lazy" className="w-full h-full object-cover" /></div>
                <div className="p-5">
                  <h3 className="font-heading font-semibold text-navy-heading text-lg">{v.name}</h3>
                  <div className="mt-2 flex items-center gap-2 text-sm text-slate-500"><Users className="w-4 h-4" /> {v.seating}</div>
                  <p className="mt-2 text-xs text-slate-500">{v.suitable_for}</p>
                  <Link to="/book-taxi" className="mt-4 block text-center bg-brand-light text-brand font-semibold text-sm py-2.5 rounded-full hover:bg-brand hover:text-white transition-colors">Book Now</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CITY-WISE */}
      <section className="section-pad bg-navy" data-testid="cities-section">
        <div className="container-x">
          <SectionHeading center light eyebrow="Where We Serve" title="City-wise Taxi Services"
            subtitle="Dedicated local expertise in each of our four primary Rajasthan destinations." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CITY_LINKS.map((c) => (
              <Link key={c.slug} to={`/${c.slug}-taxi`} data-testid={`city-card-${c.slug}`}
                className="group relative rounded-2xl overflow-hidden h-64 shadow-lg">
                <img src={CITY_IMG[c.name]} alt={`Taxi service in ${c.name}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
                <div className="absolute bottom-0 p-5 text-white">
                  <h3 className="font-heading font-bold text-xl">Taxi in {c.name}</h3>
                  <span className="text-sm text-brand-sky font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all mt-1">Explore <ArrowRight className="w-3.5 h-3.5" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TOUR PACKAGES */}
      <section className="section-pad bg-white" data-testid="packages-section">
        <div className="container-x">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <SectionHeading eyebrow="Tour & Travel" title="Rajasthan Tour Packages" subtitle="Curated multi-day tours by private taxi across royal Rajasthan." />
            <Link to="/tour-packages" className="hidden sm:inline-flex items-center gap-1 text-brand font-semibold hover:gap-2 transition-all">All packages <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {packages.slice(0, 6).map((p) => (
              <Link key={p.slug} to={`/tour-packages/${p.slug}`} data-testid={`package-card-${p.slug}`}
                className="group bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm card-hover">
                <div className="relative h-48 overflow-hidden">
                  <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3 bg-navy/90 text-white text-xs font-semibold px-3 py-1 rounded-full">{p.duration}</div>
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-semibold text-navy-heading text-lg">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">{p.short}</p>
                  <div className="mt-3 flex items-center justify-end">
                    <span className="text-sm text-brand font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">Details <ArrowRight className="w-3.5 h-3.5" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AIRPORT + OUTSTATION highlight */}
      <section className="section-pad bg-slate-50" data-testid="airport-outstation-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-8">
          {[
            { title: "Airport Taxi Services", icon: "Plane", to: "/taxi-services/airport-taxi", desc: "On-time airport pickups and drops for Jodhpur, Jaipur and Udaipur airports. We track your flight, help with luggage and get you to your destination stress-free — day or night." },
            { title: "Outstation Taxi Services", icon: "Navigation", to: "/taxi-services/outstation-taxi", desc: "Comfortable intercity travel across Rajasthan and beyond. Transparent per-kilometre fares, experienced highway drivers and well-maintained cars for long journeys." },
          ].map((b) => (
            <div key={b.title} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 card-hover">
              <div className="w-14 h-14 rounded-2xl bg-brand flex items-center justify-center mb-5"><DynIcon name={b.icon} className="w-7 h-7 text-white" /></div>
              <h3 className="font-heading font-bold text-navy-heading text-2xl mb-3">{b.title}</h3>
              <p className="text-slate-600 leading-relaxed mb-5">{b.desc}</p>
              <Link to={b.to} className="inline-flex items-center gap-2 text-brand font-semibold hover:gap-3 transition-all">Learn more <ArrowRight className="w-4 h-4" /></Link>
            </div>
          ))}
        </div>
      </section>

      <ReviewsSection />
      <FaqSection faqs={HOME_FAQS} subtitle="Answers to common questions about our taxi and tour services." />
      <CtaBand />
    </>
  );
}
